/* eslint arrow-body-style: */
import { batchActions } from 'redux-batched-actions';
import { isCancel, mustRefetch } from 'api/Helpers';
import { getSubCategories as apiGetSubCategories } from 'api/Categories';
import KioskLog from 'models/KioskLog';
import {
	IS_FETCHING,
	FETCH_ERROR,
	RECEIVE_SUB_CATEGORIES,
	TOGGLE_PRODUCTS_DISPLAY,
	TOGGLE_PRODUCTS_AND_CATEGORIES_DISPLAY,
	UPDATE_RECENT_CAT,
	UPDATE_SCROLL_POS,
} from './constants';

/**
 * Update the most recently visited category. Used when mounting the CategoriesContainer
 * to tell whether we need to reset filters.
 * @param {string} id The id of the category that we have been to most recently
 */
const updateMostRecentCat = id => ({
	type: UPDATE_RECENT_CAT,
	id,
});

/**
 * Save the scroll position of the ProductsDisplay component so we can go back to the same position later.
 * @param {number} position The offset (in pixels) of how far the ProductsDisplay component has been scrolled
 */
const updateScrollPosition = position => ({
	type: UPDATE_SCROLL_POS,
	position,
});

/**
 * Show products list instead of categories
 * @param {boolean} display
 */
const toggleProductsDisplay = display => ({
	type: TOGGLE_PRODUCTS_DISPLAY,
	displayProducts: display,
});

/**
 * Show products list instead of categories
 * @param {boolean} display
 */
const toggleProductsAndCategoriesDisplay = display => ({
	type: TOGGLE_PRODUCTS_AND_CATEGORIES_DISPLAY,
	displayProductsAndCategories: display,
});

/**
 * Specify whether we are fetching subCategories or products
 * @param {string} listType - PRODUCTS/SUB_CATEGORIES
 * @param {boolean} isFetching
 */
const updateFetchStatus = isFetching => ({
	type: IS_FETCHING,
	isFetching,
});

/**
 * Specify whether the error is for subCategories or products
 * @param {string} listType - PRODUCTS/SUB_CATEGORIES
 * @param {Error} fetchError
 */
const updateFetchError = fetchError => ({
	type: FETCH_ERROR,
	fetchError,
});

const receiveSubCategories = (categoryId, featuredImages, subCategories, cacheable) => {
	// Only store the last fetch time if the response has marked this data cacheable.
	// An undefined lastFetchTime will always trigger a refetch
	const lastFetchTime = cacheable ? Date.now() : undefined;

	return {
		type: RECEIVE_SUB_CATEGORIES,
		categoryId,
		featuredImages,
		subCategories,
		lastFetchTime,
	};
};

/**
 * Fetch a list of sub categories in a category
 * @param {axios cancel token} cancelToken
 * @param {number} categoryId
 */
const getSubCategories = (cancelToken, categoryId) => {
	return (dispatch, getState) => {
		const { subCategories } = getState();
		const lastFetchTime = subCategories.getIn(
			['subCategories', categoryId, 'lastFetchTime'],
			undefined,
		);
		if (!mustRefetch(lastFetchTime)) {
			return false;
		}

		dispatch(updateFetchStatus(true));

		return apiGetSubCategories(cancelToken, categoryId)
			.then(response => {
				dispatch(
					batchActions([
						updateFetchStatus(false),
						receiveSubCategories(
							categoryId,
							response.data.results.featuredImages,
							response.data.results.subCategories,
							response.data.cacheable,
						),
					]),
				);
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}
				const log = new KioskLog(error, 'Error file location: redux/domain/SubCategories/actions');
				log.postMessage();
				dispatch(updateFetchError(error));
			});
	};
};

export {
	getSubCategories,
	receiveSubCategories,
	toggleProductsDisplay,
	toggleProductsAndCategoriesDisplay,
	updateFetchError,
	updateFetchStatus,
	updateMostRecentCat,
	updateScrollPosition,
};
